// Aliases for native markdown components
import MarkdownLink, { MarkdownLinkProps } from '@modules/ui/markdown/components/MarkdownLink';
import MarkdownBlockQuote from '@modules/ui/markdown/components/MarkdownBlockQuote';
import MarkdownInlineCode, {
  MarkdownInlineCodeProps,
} from '@modules/ui/markdown/components/MarkdownInlineCode';
import MarkdownFigure from '@modules/ui/markdown/components/MarkdownFigure';
import MarkdownFigcaption from '@modules/ui/markdown/components/MarkdownFigcaption';
import MarkdownHeader, { HeaderProps } from '@modules/ui/markdown/components/MarkdownHeader';
import MarkdownImage from '@modules/ui/markdown/components/MarkdownImage';
import MarkdownKeyboardInput from '@modules/ui/markdown/components/MarkdownKeyboardInput';
import MarkdownListItem, { ListItemProps } from '@modules/ui/markdown/components/MarkdownListItem';
import MarkdownOrderedList from '@modules/ui/markdown/components/MarkdownOrderedList';
import MarkdownUnorderedList from '@modules/ui/markdown/components/MarkdownUnorderedList';
import MarkdownP from '@modules/ui/markdown/components/MarkdownP';
import React, { ComponentPropsWithoutRef } from 'react';
import { TTypographyProps, TableBody, TableRow, Typography } from '@rbx/ui';
import MarkdownSource from '@modules/ui/markdown/components/MarkdownSource';
import MarkdownStrong from '@modules/ui/markdown/components/MarkdownStrong';
import MarkdownTable from '@modules/ui/markdown/components/MarkdownTable';
import MarkdownTableHead from '@modules/ui/markdown/components/MarkdownTableHead';
import MarkdownTableCell from '@modules/ui/markdown/components/MarkdownTableCell';
import MarkdownVideo from '@modules/ui/markdown/components/MarkdownVideo';
import Markdown from '@modules/assistant/markdown/components/Markdown';
import ColorSwatch from '@modules/ui/markdown/components/ColorSwatch';
import UseStudioButton, {
  UseStudioButtonProps,
} from '@modules/useStudio/components/UseStudioButton';

export type AliasComponentOptions = {
  paragraphProps?: TTypographyProps;
  inlineCodeProps?: MarkdownInlineCodeProps;
  linkProps?: MarkdownLinkProps;
  listItemProps?: ListItemProps; // Used for li text content
  tableProps?: {
    tableStyle?: React.CSSProperties;
    tableRowStyle?: Record<string, unknown>;
    tableCellStyle?: React.CSSProperties;
    tableHeaderCellStyle?: React.CSSProperties;
  };
};

export const CustomizedMarkdownInlineCodeFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownInlineCode(props: MarkdownInlineCodeProps) {
    return (
      <MarkdownInlineCode
        {...(options?.inlineCodeProps ? options.inlineCodeProps : {})}
        {...props}
      />
    );
  };
};

export const CustomizedMarkdownLinkFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownLink(props: MarkdownLinkProps) {
    return <MarkdownLink {...(options?.linkProps ? options.linkProps : {})} {...props} />;
  };
};

export const CustomizedMarkdownListItemFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownListItem(props: ListItemProps) {
    return (
      <MarkdownListItem {...(options?.listItemProps ? options.listItemProps : {})} {...props} />
    );
  };
};

export const CustomizedMarkdownTableFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownTable(props: ComponentPropsWithoutRef<typeof MarkdownTable>) {
    return <MarkdownTable {...props} tableStyle={options?.tableProps?.tableStyle} />;
  };
};

export const CustomizedMarkdownTableRowFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownTableRow(props: ComponentPropsWithoutRef<typeof TableRow>) {
    return <TableRow {...props} sx={options?.tableProps?.tableRowStyle} />;
  };
};

export const CustomizedMarkdownTableCellFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownTableCell(props: ComponentPropsWithoutRef<'td'>) {
    return <MarkdownTableCell {...props} tableCellStyle={options?.tableProps?.tableCellStyle} />;
  };
};

export const CustomizedMarkdownTableHeaderCellFromOptions = (options?: AliasComponentOptions) => {
  return function CustomizedMarkdownTableHeaderCell(props: ComponentPropsWithoutRef<'th'>) {
    return (
      <MarkdownTableCell {...props} tableCellStyle={options?.tableProps?.tableHeaderCellStyle} />
    );
  };
};

const aliasComponents = (options?: AliasComponentOptions) => ({
  a: CustomizedMarkdownLinkFromOptions(options),
  blockquote: MarkdownBlockQuote,
  pre: Markdown.Pre,
  code: CustomizedMarkdownInlineCodeFromOptions(options),
  figure: MarkdownFigure,
  figcaption: MarkdownFigcaption,
  h2: (props: HeaderProps) => <MarkdownHeader {...props} variant='h2' />,
  h3: (props: HeaderProps) => <MarkdownHeader {...props} variant='h3' />,
  h4: (props: HeaderProps) => <MarkdownHeader {...props} variant='h4' />,
  h5: (props: HeaderProps) => <MarkdownHeader {...props} variant='h5' />,
  h6: (props: HeaderProps) => <MarkdownHeader {...props} variant='h6' />,
  dt: (props: ComponentPropsWithoutRef<typeof Typography>) => (
    <dt>
      <Typography {...props} variant='body1' fontWeight='bold' component='div' marginBottom={0.5} />
    </dt>
  ),
  dd: (props: ComponentPropsWithoutRef<typeof Typography>) => (
    <dt>
      <Typography {...props} variant='body1' component='div' paddingLeft={3} />
    </dt>
  ),
  img: MarkdownImage,
  inlineCode: CustomizedMarkdownInlineCodeFromOptions(options),
  kbd: MarkdownKeyboardInput,
  li: CustomizedMarkdownListItemFromOptions(options),
  ol: MarkdownOrderedList,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  p: MarkdownP(options) as any,
  source: MarkdownSource,
  strong: MarkdownStrong,
  table: CustomizedMarkdownTableFromOptions(options),
  thead: MarkdownTableHead,
  tbody: (props: ComponentPropsWithoutRef<typeof TableBody>) => <TableBody {...props} />,
  tr: CustomizedMarkdownTableRowFromOptions(options),
  td: CustomizedMarkdownTableCellFromOptions(options),
  th: CustomizedMarkdownTableHeaderCellFromOptions(options),
  ul: MarkdownUnorderedList,
  video: MarkdownVideo,
  colorswatch: ({ value, ...props }: ComponentPropsWithoutRef<typeof ColorSwatch>) => (
    <ColorSwatch value={value} {...props} />
  ),
  usestudiobutton: (props: UseStudioButtonProps) => <UseStudioButton {...props} />,
});

export default aliasComponents;
